import { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import CommonTooltip from '../Common/ToolTip';
import { IntegrationProviderIcon } from '../../utils/integrationProviderIcons';

const TOOLTIP_ICON_SIZE = 24;
const INTEGRATION_CATEGORIES = {
  projectManagement: 'Project Management',
  sourceCode: 'Source Code',
  testManagement: 'Test Case Management',
};

const INTEGRATION_THEME_STYLES = {
  light: {
    container: {
      backgroundColor: '#FFFFFF',
      border: '1px solid #D9D9D9',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    },
    row: 'bg-[#DEEFFF]',
    iconCircle: 'bg-[#FFFFFF]',
    title: 'text-[16px] font-semibold leading-none text-[#202020]',
    subtitle: 'text-[12px] font-normal leading-none text-[#3F3F3F]',
    connectLink: 'text-[#066FD1] hover:text-[#24527A]',
  },
  dark: {
    container: {
      backgroundColor: '#173A5A',
      border: '1px solid #2D3055',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
    },
    row: 'bg-[#182433]',
    iconCircle: 'bg-[#26364C]',
    title: 'text-[16px] font-semibold leading-none text-white',
    subtitle: 'text-[12px] font-normal leading-none text-[#8FA3BC]',
    connectLink: 'text-[#48A7FF] hover:text-[#7EC8FF]',
  },
};

function getToolDisplayName(isConnected, provider) {
  if (!isConnected || !provider) {
    return 'Not Connected';
  }
  return provider;
}

function IntegrationListRow({ category, displayName, provider, isConnected, theme }) {
  const styles = INTEGRATION_THEME_STYLES[theme] || INTEGRATION_THEME_STYLES.dark;

  return (
    <div
      className={`box-border flex h-14 w-full max-w-full items-center gap-5 overflow-hidden rounded-lg py-2 pr-4 pl-[10px] ${styles.row}`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.iconCircle}`}
      >
        {isConnected ? (
          <IntegrationProviderIcon provider={provider} size={TOOLTIP_ICON_SIZE} />
        ) : (
          <AlertTriangle className="h-5 w-5 text-[#F59E0B]" strokeWidth={2.5} />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span className={`truncate ${styles.title}`}>{displayName}</span>
        <span className={`mt-0.5 truncate ${styles.subtitle}`}>{category}</span>
      </div>

      {isConnected ? (
        <CheckCircle2
          className="h-5 w-5 flex-shrink-0 text-[#2FB344]"
          fill="#2FB344"
          stroke="white"
        />
      ) : (
        <Link
          to="/integration"
          className={`whitespace-nowrap text-sm font-medium flex-shrink-0 ${styles.connectLink}`}
        >
          Connect
        </Link>
      )}
    </div>
  );
}

IntegrationListRow.propTypes = {
  category: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  provider: PropTypes.string,
  isConnected: PropTypes.bool.isRequired,
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
};

function ConnectedIntegrationsPanel({
  theme,
  standupVisibility,
  isProjectManagementConnected,
  isSourceCodeConnected,
  isTestManagementConnected,
}) {
  return (
    <div className="box-border flex w-full min-w-0 max-w-full flex-col gap-2 overflow-hidden">
      <IntegrationListRow
        theme={theme}
        category={INTEGRATION_CATEGORIES.projectManagement}
        displayName={getToolDisplayName(
          isProjectManagementConnected,
          standupVisibility.ticket_source_provider,
        )}
        provider={standupVisibility.ticket_source_provider}
        isConnected={isProjectManagementConnected}
      />
      <IntegrationListRow
        theme={theme}
        category={INTEGRATION_CATEGORIES.sourceCode}
        displayName={getToolDisplayName(
          isSourceCodeConnected,
          standupVisibility.code_source_provider,
        )}
        provider={standupVisibility.code_source_provider}
        isConnected={isSourceCodeConnected}
      />
      <IntegrationListRow
        theme={theme}
        category={INTEGRATION_CATEGORIES.testManagement}
        displayName={getToolDisplayName(
          isTestManagementConnected,
          standupVisibility.test_source_provider,
        )}
        provider={standupVisibility.test_source_provider}
        isConnected={isTestManagementConnected}
      />
    </div>
  );
}

ConnectedIntegrationsPanel.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  standupVisibility: PropTypes.object.isRequired,
  isProjectManagementConnected: PropTypes.bool.isRequired,
  isSourceCodeConnected: PropTypes.bool.isRequired,
  isTestManagementConnected: PropTypes.bool.isRequired,
};

function useIntegrationVisibility() {
  const standupVisibility = useSelector((state) => state.jira?.standupVisibility) || {};

  const isProjectManagementConnected = standupVisibility.ticket_source === true;
  const isSourceCodeConnected = standupVisibility.code_source === true;
  const isTestManagementConnected = standupVisibility.test_source === true;

  return {
    standupVisibility,
    isProjectManagementConnected,
    isSourceCodeConnected,
    isTestManagementConnected,
  };
}

export function IntegrationsHeaderButton() {
  const theme = useSelector((state) => state.theme.theme);
  const themeStyles = INTEGRATION_THEME_STYLES[theme] || INTEGRATION_THEME_STYLES.dark;
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const containerRef = useRef(null);
  const {
    standupVisibility,
    isProjectManagementConnected,
    isSourceCodeConnected,
    isTestManagementConnected,
  } = useIntegrationVisibility();

  useEffect(() => {
    if (!isPanelOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsPanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanelOpen]);

  return (
    <div ref={containerRef} className="relative z-[100] shrink-0">
      <CommonTooltip content="Integration" position="bottom">
        <button
          type="button"
          aria-label="Integration"
          aria-expanded={isPanelOpen}
          onClick={() => setIsPanelOpen((previousValue) => !previousValue)}
          className={`flex h-[34px] w-[34px] items-center justify-center rounded-md border transition-colors ${
            theme === 'light'
              ? 'border-[#A6C3DC] bg-[#FFFFFF] hover:bg-[#F5F5F5]'
              : 'border-[#25384F] bg-[#0F0F0F] hover:bg-[#1E2B3A]'
          }`}
        >
          <Icon
            icon="carbon:ibm-webmethods-hybrid-integration"
            className="h-7 w-7 text-[#24527A] dark:text-[#066FD1]"
            aria-hidden="true"
          />
        </button>
      </CommonTooltip>

      {isPanelOpen && (
        <div
          className="absolute right-0 top-[42px] box-border w-[282px] overflow-hidden rounded-[10px] p-2"
          style={{
            backgroundColor: themeStyles.container.backgroundColor,
            border: themeStyles.container.border,
            boxShadow: themeStyles.container.boxShadow,
            maxWidth: 'min(282px, calc(100vw - 24px))',
          }}
      >
          <ConnectedIntegrationsPanel
            theme={theme}
            standupVisibility={standupVisibility}
            isProjectManagementConnected={isProjectManagementConnected}
            isSourceCodeConnected={isSourceCodeConnected}
            isTestManagementConnected={isTestManagementConnected}
          />
        </div>
      )}
    </div>
  );
}

function StandUpIntegratedToolsSection() {
  const theme = useSelector((state) => state.theme.theme);
  const themeStyles = INTEGRATION_THEME_STYLES[theme] || INTEGRATION_THEME_STYLES.dark;
  const {
    standupVisibility,
    isProjectManagementConnected,
    isSourceCodeConnected,
    isTestManagementConnected,
  } = useIntegrationVisibility();

  return (
    <div className="w-full px-5 pb-2 pt-1" aria-expanded>
      <div
        className="box-border flex w-[282px] flex-col gap-2 overflow-hidden rounded-[10px] p-2"
        style={{
          backgroundColor: themeStyles.container.backgroundColor,
          border: themeStyles.container.border,
          boxShadow: themeStyles.container.boxShadow,
        }}
      >
        <IntegrationListRow
          theme={theme}
          category={INTEGRATION_CATEGORIES.projectManagement}
          displayName={getToolDisplayName(
            isProjectManagementConnected,
            standupVisibility.ticket_source_provider,
          )}
          provider={standupVisibility.ticket_source_provider}
          isConnected={isProjectManagementConnected}
        />
        <IntegrationListRow
          theme={theme}
          category={INTEGRATION_CATEGORIES.sourceCode}
          displayName={getToolDisplayName(
            isSourceCodeConnected,
            standupVisibility.code_source_provider,
          )}
          provider={standupVisibility.code_source_provider}
          isConnected={isSourceCodeConnected}
        />
        <IntegrationListRow
          theme={theme}
          category={INTEGRATION_CATEGORIES.testManagement}
          displayName={getToolDisplayName(
            isTestManagementConnected,
            standupVisibility.test_source_provider,
          )}
          provider={standupVisibility.test_source_provider}
          isConnected={isTestManagementConnected}
        />
      </div>
    </div>
  );
}

export default memo(StandUpIntegratedToolsSection);
